import React, { PureComponent, Children } from 'react'
import { number, node, oneOfType, arrayOf, func, shape } from 'prop-types'
import { Link } from '@ycos/primitives'
import get from 'lodash/get'
import { createComponent } from '@ycos/fela'
import { Picture } from '@ycos/picture'
import StandardVideo from '@ycos/component-video'
import Block from '@ycos/component-block'
import Paginator from './Paginator'
import NavigationButtons from './NavigationButtons'
import ContentCarouselItem from './ContentCarouselItem'
import { hasTag, getExpandedMediaItem } from '@ycos/coremedia-renderer'

const SWIPE_THRESHOLD = 150

class ContentCarousel extends PureComponent {
  static layoutVariant = 'isc-content-carousel'

  static getDisabledState(currentIndex, visibleItems, childrenCount) {
    return {
      disablePrevious: currentIndex === 0,
      disableNext: currentIndex >= childrenCount - visibleItems
    }
  }

  static getTouchPosition(event) {
    const { targetTouches } = event
    const target = targetTouches && targetTouches.length ? targetTouches[0] : event
    return target.clientX
  }

  static getPagination(currentIndex, visibleItems, childrenCount) {
    const pages = Math.ceil(childrenCount / visibleItems)
    const currentPage = Math.floor(currentIndex % childrenCount) / visibleItems + 1

    return { currentPage, pages }
  }

  static wrapWithLink = ({ target }) => ({ children, ...rest }) => {
    if (target) {
      return (
        <Link data-seo-segment={target.seoSegment} href={target.href} {...rest}>
          {children}
        </Link>
      )
    }
    return children
  }

  constructor(props) {
    super(props)
    const autoAnimateTag = hasTag(props.item.tags, 'ContentCarousel.AutoAnimate')
    this.state = {
      currentIndex: props.slideIndex,
      lastIndex: 0,
      startX: 0,
      hasSwiped: false,
      ...ContentCarousel.getDisabledState(props.slideIndex, props.visibleItems, Children.count(props.children)),
      autoAnimate: autoAnimateTag && setInterval(this.autoAnimate, 8000)
    }
  }

  componentDidMount() {
    const { currentIndex } = this.state
    const { onPageChange, visibleItems, children } = this.props
    const childrenCount = Children.count(children)

    if (onPageChange) {
      onPageChange(ContentCarousel.getPagination(currentIndex, visibleItems, childrenCount))
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const { currentIndex } = nextState
    const { onPageChange, visibleItems, children } = nextProps
    const childrenCount = Children.count(children)

    if (this.state.currentIndex !== currentIndex && onPageChange) {
      onPageChange(ContentCarousel.getPagination(currentIndex, visibleItems, childrenCount))
    }
  }

  componentWillUnmount() {
    this.handleMouseLeave()
    this.stopAutoAnimate()
  }

  autoAnimate = () => {
    const { currentIndex } = this.state
    const { children } = this.props
    const childrenCount = Children.count(children)

    if (currentIndex >= childrenCount - 1) {
      this.gotoSlide(0)
      this.stopAutoAnimate()
    } else {
      this.gotoSlide(Math.max(0, this.state.currentIndex + this.props.visibleItems))
    }
  }

  stopAutoAnimate = () => {
    if (this.state.autoAnimate) {
      clearInterval(this.state.autoAnimate)
      this.setState({ autoAnimate: false })
    }
  }

  handleKeyUp = (e) => {
    if (e.keyCode === 39) {
      this.handleNextClick()
    } else if (e.keyCode === 37) {
      this.handlePreviousClick()
    }
  }

  handleMouseEnter = () => {
    document.body.addEventListener('keyup', this.handleKeyUp)
  }

  handleMouseLeave = () => {
    document.body.removeEventListener('keyup', this.handleKeyUp)
  }

  handleNextClick = () => {
    if (this.state.disableNext) {
      return
    }
    this.stopAutoAnimate()
    this.gotoSlide(this.state.currentIndex + 1)
  }

  handlePreviousClick = () => {
    if (this.state.disablePrevious) {
      return
    }
    this.stopAutoAnimate()
    this.gotoSlide(this.state.currentIndex - 1)
  }

  gotoSlide = (slideIndex) => {
    const { visibleItems, children } = this.props
    this.setState((prevState) => {
      const currentIndex = slideIndex
      return {
        currentIndex,
        lastIndex: prevState.currentIndex,
        ...ContentCarousel.getDisabledState(currentIndex, visibleItems, Children.count(children))
      }
    })
  }

  handlePageChange = (page) => {
    const { visibleItems, children } = this.props
    const currentIndex = page * visibleItems
    this.setState((prevState) => ({
      currentIndex,
      lastIndex: prevState.currentIndex,
      ...ContentCarousel.getDisabledState(currentIndex, visibleItems, Children.count(children))
    }))
  }

  resetPositionState = (startX = 0) => {
    this.setState({ startX })
  }

  handleTouchStart = (event) => {
    this.setState({ isTouch: true })

    this.handleMoveStart(event)
  }

  handleMoveStart = (event) => {
    this.resetPositionState(ContentCarousel.getTouchPosition(event))
  }

  handleMove = (event) => {
    const clientX = ContentCarousel.getTouchPosition(event)
    const { startX, hasSwiped } = this.state
    const posX = clientX - startX

    this.stopAutoAnimate()

    // only activates the auto swipe if the user hasn't interacted with the carousel
    if (!hasSwiped && posX < -SWIPE_THRESHOLD) {
      this.handleNextClick()
      this.setState({ hasSwiped: true })
    } else if (!hasSwiped && posX > SWIPE_THRESHOLD) {
      this.handlePreviousClick()
      this.setState({ hasSwiped: true })
    }
  }

  handleMoveEnd = () => {
    this.setState({ hasSwiped: false })
    this.resetPositionState()
  }

  renderItem = (child, i, transitionType = 'swipe') => {
    const { visibleItems, animation } = this.props
    const { currentIndex } = this.state
    const itemWidth = 100 / visibleItems
    const isOffsetRight = i >= currentIndex + visibleItems
    const isOffsetLeft = i < currentIndex
    const isVideo = get(child, 'props.item.layoutVariant') === 'isc-video'
    const visible = !isOffsetLeft && !isOffsetRight
    const offset = Math.floor(i / visibleItems) * -100
    let translate = 0

    if (isOffsetLeft) {
      translate = -animation.offset
    } else if (isOffsetRight) {
      translate = animation.offset
    }

    return (
      <ContentCarouselItem
        data-automation="carousel-item"
        offset={offset}
        translate={translate}
        visible={visible}
        width={itemWidth}
        transitionType={transitionType}
      >
        {isVideo ? React.cloneElement(child, { visible }) : child}
      </ContentCarouselItem>
    )
  }

  renderBlockText = (item) => (
    <BlockContainer>
      <Block
        item={{
          ...item,
          layoutVariant: 'isc-block',
          pictures: [] // Pictures set to empty array as this is used just to render block text
        }}
      />
    </BlockContainer>
  )

  render() {
    const { children, margin, visibleItems, item } = this.props
    const { currentIndex, disableNext, disablePrevious } = this.state
    const childrenCount = Children.count(children)
    const showPaginator = hasTag(item.tags, 'ContentCarousel.ShowPaginator')
    const hideControls = hasTag(item.tags, 'ContentCarousel.hideControls')
    const pagination = ContentCarousel.getPagination(currentIndex, visibleItems, childrenCount)

    return (
      <Container
        data-automation="content-carousel"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onTouchMove={this.handleMove}
        onTouchStart={this.handleTouchStart}
        onTouchEnd={this.handleMoveEnd}
      >
        <ContentWrapper data-automation="content-carousel-wrapper">
          <Content>
            <CarouselMain data-automation="content-carousel-main">
              <Row margin={margin}>
                {item.items.length &&
                  Children.map(children, (child, i) => {
                    const { pictures = [] } = getExpandedMediaItem(child.props.item)

                    if (pictures.length) {
                      const WrappedLink = ContentCarousel.wrapWithLink({ target: child.props.item.target })
                      return this.renderItem(
                        <WrappedLink>
                          <Picture images={pictures} />
                        </WrappedLink>,
                        i,
                        'swipe'
                      )
                    } else if (child.props.item.layoutVariant === 'isc-video') {
                      return this.renderItem(<StandardVideo {...child.props} />, i, 'swipe')
                    }
                    return this.renderItem(child, i, 'swipe')
                  })}
              </Row>
              <Paginator {...pagination} onPageChange={this.handlePageChange} showPaginator={showPaginator} />
            </CarouselMain>
            {!hideControls && (
              <NavigationButtons
                handlePreviousClick={this.handlePreviousClick}
                disablePrevious={disablePrevious}
                handleNextClick={this.handleNextClick}
                disableNext={disableNext}
              />
            )}
            {Children && (
              <Row margin={margin} data-automation="content-carousel-text">
                {Children.map(children, (child, i) => {
                  return this.renderItem(this.renderBlockText(child.props.item), i, 'fade')
                })}
              </Row>
            )}
            {this.renderBlockText(item)}
          </Content>
        </ContentWrapper>
      </Container>
    )
  }
}

ContentCarousel.propTypes = {
  slideIndex: number,
  margin: number,
  visibleItems: number,
  onPageChange: func,
  animation: shape({
    offset: number
  }),
  children: oneOfType([node, arrayOf(node)]).isRequired
}

ContentCarousel.defaultProps = {
  slideIndex: 0,
  visibleItems: 1,
  margin: 20,
  animation: {
    offset: 100
  },
  onPageChange: null
}

const Container = createComponent(
  'Container',
  () => ({
    position: 'relative'
  }),
  'div',
  ['onMouseEnter', 'onMouseLeave', 'onTouchMove', 'onTouchStart', 'onTouchEnd']
)

const CarouselMain = createComponent('CarouselMain', ({ theme }) => ({
  overflow: 'hidden',
  marginBottom: `${4 * theme.spacingMultiplier}px`,
  'screen-medium': {
    marginBottom: `${3 * theme.spacingMultiplier}px`
  },
  position: 'relative'
}))

const Content = createComponent('Content', () => ({
  position: 'relative',
  overflow: 'hidden'
}))

const ContentWrapper = createComponent('ContentWrapper', () => ({}))

const Row = createComponent('Row', () => ({
  zIndex: 0,
  position: 'relative',
  display: 'flex',
  overflow: 'visible',
  width: '100%',
  justifyContent: 'flex-start'
}))

const BlockContainer = createComponent('BlockContainer', ({ theme }) => ({
  paddingRight: `${7 * theme.spacingMultiplier}px`
}))

export default ContentCarousel
